import { all, call, cancel, fork, put, take, takeEvery, takeLatest } from 'redux-saga/effects';
import SuperFetch from 'library/helpers/superFetch';
import describeApiError from 'library/helpers/describeApiError';
import { Notification } from 'zComponents/atoms';
import { OPEN_ACCESS_DEVICE_ACTION } from 'config/accessWindow';
import { accessActions } from 'reduxStore/access/slice';
import { accessWindowsActions } from 'reduxStore/accessWindows/slice';
import { peopleActions } from 'reduxStore/people/slice';
import { openAccessActions as A } from './slice';

const SLUG = '/accessWindows';

// The server takes at most 50 locks per POST; each lock is a ~30s gateway call.
const DEVICE_BATCH = 50;

const chunk = (items, size) => {
	const out = [];
	for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
	return out;
};

// Serializable error for the store: the server's message, and its status for the 403 state.
const toError = (res, fallback) => ({
	status: res?.statusCode ?? null,
	message: describeApiError(res, null) || res?.info || fallback
});

// One call for the whole page: every schedule with its devices and people.
export function* fetchSchedulesWorker() {
	try {
		const res = yield call(() => SuperFetch.get(`${SLUG}/open-access`));
		if (res.statusCode !== 200) {
			yield put(A.schedulesFailed(toError(res, "Couldn't load schedules.")));
			return;
		}
		yield put(A.schedulesReceived(res.data ?? {}));
	} catch (error) {
		yield put(A.schedulesFailed({ status: null, message: error.message || "Couldn't load schedules." }));
	}
}

// A refused request fails every lock in it alike; retrying the same body fails the same way.
const refusedRows = (deviceIDs, reason, retryable = false) =>
	deviceIDs.map((deviceID) => ({ deviceID, success: false, retryable, reason }));

function* addDevices(accessWindowID, deviceIDs, action) {
	for (const batch of chunk(deviceIDs, DEVICE_BATCH)) {
		let rows;
		try {
			const res = yield call(() =>
				SuperFetch.post(`${SLUG}/${accessWindowID}/devices`, { deviceIDs: batch, action })
			);
			rows =
				res.statusCode === 200
					? res.data || []
					: refusedRows(batch, toError(res, 'The request was refused.').message).map((r) => ({
							...r,
							action
						}));
		} catch (error) {
			rows = refusedRows(batch, error.message || 'The request could not be sent.', true).map((r) => ({
				...r,
				action
			}));
		}
		yield put(A.scheduleDevicesSaveRowsReceived({ accessWindowID, rows }));
	}
}

function* removeDevices(accessWindowID, deviceIDs) {
	for (const deviceID of deviceIDs) {
		let row;
		try {
			const res = yield call(() => SuperFetch.delete(`${SLUG}/${accessWindowID}/devices/${deviceID}`));
			row =
				res.statusCode === 200
					? { ...res.data, deviceID, removed: true }
					: {
							deviceID,
							removed: true,
							success: false,
							retryable: false,
							reason: toError(res, 'The request was refused.').message
						};
		} catch (error) {
			row = { deviceID, removed: true, success: false, retryable: true, reason: error.message };
		}
		yield put(A.scheduleDevicesSaveRowsReceived({ accessWindowID, rows: [row] }));
	}
}

// Links (up to 50 locks per request) then releases one by one; rows land on the card as they come.
export function* saveScheduleDevicesWorker({ payload }) {
	const { accessWindowID, add = [], remove = [] } = payload;
	const total = add.length + remove.length;
	if (total > 1) {
		Notification('info', `Updating ${total} locks…`, 'This can take a moment.');
	}
	try {
		// One POST per action: a linked lock sent with a new action has its rules replaced.
		const byAction = new Map();
		add.forEach(({ deviceID, action = OPEN_ACCESS_DEVICE_ACTION.HOLD }) =>
			byAction.set(action, [...(byAction.get(action) ?? []), deviceID])
		);
		for (const [action, deviceIDs] of byAction) yield call(addDevices, accessWindowID, deviceIDs, action);
		if (remove.length) yield call(removeDevices, accessWindowID, remove);
	} finally {
		yield put(A.scheduleDevicesSaveFinished({ accessWindowID }));
		yield put(A.schedulesRequested());
		yield put(accessWindowsActions.loadAccessWindows());
	}
}

function* requestSchedules() {
	yield put(A.schedulesRequested());
}

function* pageSession() {
	yield fork(function* watch() {
		yield all([
			takeLatest(A.schedulesRequested.type, fetchSchedulesWorker),
			// A schedule saved from AddScheduleModal re-derives its locks server-side; read the result back.
			takeLatest(
				[
					accessWindowsActions.createAccessWindowSuccess.type,
					accessWindowsActions.updateAccessWindowSuccess.type,
					accessWindowsActions.deleteAccessWindowSuccess.type
				],
				requestSchedules
			),
			// A grant changed from Manage access moves people on or off a schedule's card.
			takeLatest(
				[
					peopleActions.manageAccessBulkSuccess.type,
					accessActions.grantPermissionSuccess.type,
					accessActions.revokePermissionSuccess.type,
					accessActions.revokePermissionByIDsSuccess.type,
					accessActions.savePermissionChangesSuccess.type
				],
				requestSchedules
			)
		]);
	});
	yield call(requestSchedules);
}

// pageMounted opens the session and pageUnmounted cancels it, so nothing fetches off the page.
function* openAccessRoot() {
	while (true) {
		yield take(A.pageMounted.type);
		const session = yield fork(pageSession);
		yield take(A.pageUnmounted.type);
		yield cancel(session);
	}
}

export default function* rootSaga() {
	yield all([
		fork(openAccessRoot),
		// Not session-scoped: a lock write outlives the page and still reports when the user returns.
		takeEvery(A.scheduleDevicesSaveRequested.type, saveScheduleDevicesWorker)
	]);
}
